import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Grid,
    Segment,
    Header,
    Button,
    Form,
    Input,
    Dropdown,
    TextArea,
    Checkbox,
    Icon,
    Message,
    Loader,
    Divider,
    Modal,
    Table,
} from "semantic-ui-react";
import ReactMarkdown from "react-markdown";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
    User,
    PledgeClass,
    Family,
    UserCreateRequest,
    UserUpdateRequest,
    UserHistory,
    GetPledgeClasses,
    GetFamilies,
    GetUsersForDropdown,
    GetUsers,
    GetUser,
    GetUserHistory,
    CreateUser,
    UpdateUser,
} from "../Utils/UserAPI";
import { LoginInfo } from "../Utils/Login";

interface UserManagementProps {
    loginInfo: LoginInfo;
}

export default function UserManagement({ loginInfo }: UserManagementProps) {
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: any } | null>(null);
    const [canEdit, setCanEdit] = useState(false);
    const [canCreate, setCanCreate] = useState(false);
    const [hasAdminPermissions, setHasAdminPermissions] = useState(false);
    const [isMyAccountEditMode, setIsMyAccountEditMode] = useState(false);

    // History states
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [userHistory, setUserHistory] = useState<UserHistory[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Convert legacy HTML bold tags to markdown format
    const convertLegacyText = (text: string): string => {
        return text
            .replace(/<b>/g, '**')
            .replace(/<\/b>/g, '**');
    };

    // Check if we're in view mode (URL is /users/:id, not /users/:id/edit or /users/create)
    // On /myaccount route, we start in view mode but can switch to edit mode
    const isViewMode = location.pathname === "/myaccount" ? !isMyAccountEditMode :
        location.pathname === "/users/create" ? false :
            !location.pathname.match(/\/users\/\d+\/edit$/);

    // Helper function to check if we're in create mode
    const isCreateMode = location.pathname === "/users/create";

    // Helper function to get the actual user ID (null if create mode, number if edit/view mode)
    const actualUserId = isCreateMode ? null : (id && !isNaN(parseInt(id)) ? parseInt(id) : null);



    // If we're trying to edit but don't have permission, redirect to view mode
    useEffect(() => {
        // Only check permissions after they've been loaded from the API
        // Also ensure we're not in the middle of loading permissions
        if (!isViewMode && actualUserId && !canEdit && !loading) {
            history.push(`/users/${actualUserId}`);
        }
    }, [isViewMode, actualUserId, canEdit, history, loading, location.pathname]);

    // If we're trying to create but don't have permission, redirect to user list
    useEffect(() => {
        // Don't redirect on /myaccount route - users should always be able to view their own account
        // Also don't redirect until permissions are loaded to avoid race conditions
        if (isCreateMode && !canCreate && !loading) {
            history.push('/users');
        }
    }, [isCreateMode, canCreate, history, loading]);

    // Data states
    const [pledgeClasses, setPledgeClasses] = useState<PledgeClass[]>([]);
    const [families, setFamilies] = useState<Family[]>([]);
    const [users, setUsers] = useState<User[]>([]);

    // Form states
    const [userForm, setUserForm] = useState<UserCreateRequest>({
        first_name: "",
        last_name: "",
        init_num: 0,
        mit_alum_id: "",
        year: new Date().getFullYear(),
        chapter: "LP",
        pledge_class_id: 0,
        big_brother_user_id: 0,
        family_id: 0,
        privacy_setting: "lp only",
        is_corporation_member: false,
        company: "",
        title: "",
        email: "",
        type: "normal",
        send_monthly_email: "true",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        home_phone: "",
        work_phone: "",
        cell_phone: "",
        web_page: "",
        notes: "",
        admin_notes: "",
        alternate_email_1: "",
        alternate_email_2: "",
    });



    const loadInitialData = useCallback(async () => {


        // Load pledge classes
        GetPledgeClasses((statusCode, data) => {
            if (statusCode === 200) {
                setPledgeClasses(data);
            }
        });

        // Load families
        GetFamilies((statusCode, data) => {
            if (statusCode === 200) {
                setFamilies(data);
            }
        });

        // Load users for big brother dropdown
        GetUsersForDropdown((statusCode, data) => {
            if (statusCode === 200) {
                setUsers(data);
            }
        });

        // Check if user can create users (only needed for create mode)
        if (isCreateMode) {
            GetUsers((statusCode: number, data: User[], canCreateUsers?: boolean) => {
                if (statusCode === 200) {
                    setCanCreate(!!canCreateUsers);
                    setLoading(false); // only data needed for create mode
                } else {
                    setCanCreate(false);
                    setMessage({
                        type: "error",
                        text: "Failed to check creation permissions. Please try again."
                    });
                    setLoading(false);
                }
            });
        } else if (location.pathname === "/myaccount") {
            // On /myaccount route, users should always be able to view their own account
            setCanCreate(true);
        }
    }, [location.pathname, isCreateMode]);

    const loadUser = useCallback(async (userId: number) => {
        GetUser(userId, (statusCode, user, canEditUser, hasAdminPerms) => {
            if (statusCode === 200 && user) {
                setUserForm({
                    first_name: user.first_name,
                    last_name: user.last_name,
                    init_num: user.init_num,
                    mit_alum_id: user.mit_alum_id,
                    year: user.year,
                    chapter: user.chapter,
                    pledge_class_id: user.pledge_class_id,
                    big_brother_user_id: user.big_brother_user_id,
                    family_id: user.family_id,
                    privacy_setting: user.privacy_setting,
                    is_corporation_member: user.is_corporation_member,
                    company: user.company,
                    title: user.title,
                    email: user.email,
                    type: user.type,
                    send_monthly_email: user.send_monthly_email,
                    address_line_1: user.address_line_1,
                    address_line_2: user.address_line_2,
                    city: user.city,
                    state: user.state,
                    zip: user.zip,
                    country: user.country,
                    home_phone: user.home_phone,
                    work_phone: user.work_phone,
                    cell_phone: user.cell_phone,
                    web_page: user.web_page,
                    notes: user.notes,
                    admin_notes: user.admin_notes,
                    alternate_email_1: user.alternate_email_1,
                    alternate_email_2: user.alternate_email_2,
                });

                // On /myaccount route, users should always be able to edit their own account
                if (location.pathname === "/myaccount" && userId === loginInfo.id) {
                    setCanEdit(true);
                } else {
                    setCanEdit(canEditUser || false);
                }

                // Set admin permissions
                setHasAdminPermissions(hasAdminPerms || false);

                setLoading(false);
            }
        });
    }, [location.pathname, loginInfo.id]);

    const loadUserHistory = useCallback(async (userId: number) => {
        setHistoryLoading(true);
        GetUserHistory(userId, (statusCode, history) => {
            setHistoryLoading(false);
            if (statusCode === 200) {
                setUserHistory(history);
            } else {
                setMessage({ type: "error", text: "Failed to load user history" });
            }
        });
    }, []);

    // Load initial data
    useEffect(() => {
        loadInitialData();

        // Check if this is the myaccount route
        if (location.pathname === "/myaccount") {
            // Load current user's profile
            loadUser(loginInfo.id);
        } else if (actualUserId) {
            // Load specific user by ID
            loadUser(actualUserId);
        } else if (isCreateMode) {
            // For create mode, permissions are loaded in loadInitialData
            // No need to do anything here
        }
    }, [actualUserId, loadInitialData, location.pathname, loginInfo.id, loadUser, isCreateMode]);

    const handleFormChange = (field: keyof UserCreateRequest, value: any) => {
        setUserForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        setMessage(null);

        // Determine the user ID to update (either from URL params or current user for /myaccount)
        const userIdToUpdate = actualUserId || (location.pathname === "/myaccount" ? loginInfo.id : null);

        if (userIdToUpdate) {
            // Update existing user
            const updateData: UserUpdateRequest = { ...userForm, id: userIdToUpdate };
            UpdateUser(userIdToUpdate, updateData, (statusCode, user) => {
                if (statusCode === 200) {
                    setMessage({ type: "success", text: "User updated successfully!" });
                    if (location.pathname === "/myaccount") {
                        // On /myaccount, stay on the same page and switch back to view mode
                        setTimeout(() => {
                            setIsMyAccountEditMode(false);
                            setMessage(null);
                        }, 2000);
                    } else {
                        // On other routes, redirect to user's read-only view
                        setTimeout(() => history.push(`/users/${userIdToUpdate}`), 2000);
                    }
                } else {
                    setMessage({ type: "error", text: "Failed to update user. Please try again." });
                }
                setLoading(false);
            });
        } else {
            // Create new user
            CreateUser(userForm, (statusCode, user) => {
                if (statusCode === 201) {
                    setMessage({ type: "success", text: <span>User created successfully! Click <a href={`/users/${user?.id}`}>here</a> to view the user.</span> });
                    setUserForm({
                        first_name: "",
                        last_name: "",
                        init_num: 0,
                        mit_alum_id: "",
                        year: new Date().getFullYear(),
                        chapter: "LP",
                        pledge_class_id: 0,
                        big_brother_user_id: 0,
                        family_id: 0,
                        privacy_setting: "lp only",
                        is_corporation_member: false,
                        company: "",
                        title: "",
                        email: "",
                        type: "normal",
                        send_monthly_email: "true",
                        address_line_1: "",
                        address_line_2: "",
                        city: "",
                        state: "",
                        zip: "",
                        country: "",
                        home_phone: "",
                        work_phone: "",
                        cell_phone: "",
                        web_page: "",
                        notes: "",
                        admin_notes: "",
                        alternate_email_1: "",
                        alternate_email_2: "",
                    });
                } else {
                    setMessage({ type: "error", text: "Failed to create user. Please try again." });
                }
                setLoading(false);
            });
        }
    };


    const renderUserForm = () => {
        if (isViewMode) {
            return (
                <Segment>
                    {message && (
                        <Message
                            positive={message.type === "success"}
                            negative={message.type === "error"}
                            header={message.type === "success" ? "Success!" : "Error"}
                            content={message.text}
                        />
                    )}

                    <Header as="h4">Basic Information</Header>
                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <div><strong>First Name:</strong> {userForm.first_name}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Last Name:</strong> {userForm.last_name}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Class Year:</strong> {userForm.year}</div>
                        </Grid.Column>
                    </Grid>

                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <div><strong>Chapter:</strong> {userForm.chapter}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Pledge Class:</strong> {pledgeClasses.find(pc => pc.id === userForm.pledge_class_id)?.name || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Family:</strong> {families.find(f => f.id === userForm.family_id)?.name || "None"}</div>
                        </Grid.Column>
                    </Grid>

                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <div><strong>Big Brother:</strong> {users.find(u => u.id === userForm.big_brother_user_id) ? `${users.find(u => u.id === userForm.big_brother_user_id)?.first_name} ${users.find(u => u.id === userForm.big_brother_user_id)?.last_name}` : "None"}</div>
                        </Grid.Column>
                        {hasAdminPermissions && (
                            <Grid.Column>
                                <div><strong>Initiation Number:</strong> {userForm.init_num || "None"}</div>
                            </Grid.Column>
                        )}
                        {hasAdminPermissions && (
                            <Grid.Column>
                                <div><strong>MIT Alum ID:</strong> {userForm.mit_alum_id || "None"}</div>
                            </Grid.Column>
                        )}
                    </Grid>

                    <Header as="h4">Contact Information</Header>
                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <div><strong>Email:</strong> {userForm.email}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Alternate Email 1:</strong> {userForm.alternate_email_1 || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Alternate Email 2:</strong> {userForm.alternate_email_2 || "None"}</div>
                        </Grid.Column>
                    </Grid>

                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <div><strong>Home Phone:</strong> {userForm.home_phone || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Work Phone:</strong> {userForm.work_phone || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Cell Phone:</strong> {userForm.cell_phone || "None"}</div>
                        </Grid.Column>
                    </Grid>

                    <Header as="h4">Address</Header>
                    <Grid columns={2} stackable>
                        <Grid.Column>
                            <div><strong>Address Line 1:</strong> {userForm.address_line_1 || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Address Line 2:</strong> {userForm.address_line_2 || "None"}</div>
                        </Grid.Column>
                    </Grid>

                    <Grid columns={4} stackable>
                        <Grid.Column>
                            <div><strong>City:</strong> {userForm.city || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>State:</strong> {userForm.state || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>ZIP Code:</strong> {userForm.zip || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Country:</strong> {userForm.country || "None"}</div>
                        </Grid.Column>
                    </Grid>

                    <Header as="h4">Professional Information</Header>
                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <div><strong>Company:</strong> {userForm.company || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Job Title:</strong> {userForm.title || "None"}</div>
                        </Grid.Column>
                        <Grid.Column>
                            <div><strong>Web Page:</strong> {userForm.web_page || "None"}</div>
                        </Grid.Column>
                    </Grid>

                    {/* Privacy section - only show for own user or if user admin */}
                    {(location.pathname === "/myaccount" || hasAdminPermissions) && (
                        <>
                            <Header as="h4">Settings</Header>
                            <Grid columns={2} stackable>
                                <Grid.Column>
                                    <div><strong>Privacy Setting:</strong> {userForm.privacy_setting}</div>
                                </Grid.Column>
                                <Grid.Column>
                                    <div>
                                        <strong>Quarterly Emails:</strong> {userForm.send_monthly_email === "true"
                                            ? "Send quarterly emails"
                                            : "Don't send quarterly emails"}
                                    </div>
                                </Grid.Column>
                            </Grid>
                        </>
                    )}

                    {hasAdminPermissions && (
                        <div><strong>Is Corporation Member:</strong> {userForm.is_corporation_member ? "Yes" : "No"}</div>
                    )}
                    {hasAdminPermissions && (
                        <div><strong>User Type:</strong> {userForm.type}</div>
                    )}

                    <Header as="h4">Additional Information</Header>
                    <Grid columns={hasAdminPermissions ? 2 : 1} stackable>
                        <Grid.Column>
                            <div><strong>Notes:</strong> {userForm.notes || "None"}</div>
                        </Grid.Column>
                        {hasAdminPermissions && (
                            <Grid.Column>
                                <div><strong>Admin Notes:</strong> {userForm.admin_notes || "None"}</div>
                            </Grid.Column>
                        )}
                    </Grid>
                </Segment>
            );
        }

        return (
            <Segment>
                {message && (
                    <Message
                        positive={message.type === "success"}
                        negative={message.type === "error"}
                        header={message.type === "success" ? "Success!" : "Error"}
                        content={message.text}
                    />
                )}

                <Form onSubmit={handleSubmit}>
                    <Header as="h4">Basic Information</Header>
                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <Form.Field required>
                                <label>First Name</label>
                                <Input
                                    placeholder="First Name"
                                    value={userForm.first_name}
                                    onChange={(e, { value }) => handleFormChange("first_name", value)}
                                    required
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field required>
                                <label>Last Name</label>
                                <Input
                                    placeholder="Last Name"
                                    value={userForm.last_name}
                                    onChange={(e, { value }) => handleFormChange("last_name", value)}
                                    required
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field required>
                                <label>Class Year</label>
                                <Input
                                    type="number"
                                    placeholder="Year"
                                    value={userForm.year}
                                    onChange={(e, { value }) => handleFormChange("year", parseInt(value) || 0)}
                                    required
                                />
                                <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                    Please enter a four-digit year.
                                </div>
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <Form.Field required>
                                <label>Chapter</label>
                                <Input
                                    placeholder="Chapter (e.g., LP)"
                                    value={userForm.chapter}
                                    onChange={(e, { value }) => handleFormChange("chapter", value)}
                                    required
                                />
                                <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                    Please enter "LP" for Lambda Phi, or your chapter name (ie "Union").
                                </div>
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>LP Pledge Class</label>
                                <Dropdown
                                    placeholder="Select Pledge Class"
                                    fluid
                                    search
                                    selection
                                    clearable
                                    options={[
                                        { key: 0, text: "None", value: 0 },
                                        ...pledgeClasses.map(pc => ({
                                            key: pc.id,
                                            text: `${pc.name} (${pc.pledge_year})`,
                                            value: pc.id
                                        }))
                                    ]}
                                    value={userForm.pledge_class_id || 0}
                                    onChange={(e, { value }) => handleFormChange("pledge_class_id", value || 0)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Family</label>
                                <Dropdown
                                    placeholder="Select Family"
                                    fluid
                                    search
                                    selection
                                    clearable
                                    options={[
                                        { key: 0, text: "None", value: 0 },
                                        ...families.map(f => ({
                                            key: f.id,
                                            text: f.name,
                                            value: f.id
                                        }))
                                    ]}
                                    value={userForm.family_id || 0}
                                    onChange={(e, { value }) => handleFormChange("family_id", value || 0)}
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <Form.Field>
                                <label>Big Brother</label>
                                <Dropdown
                                    placeholder="Select Big Brother"
                                    fluid
                                    search
                                    selection
                                    clearable
                                    options={[
                                        { key: 0, text: "None", value: 0 },
                                        ...users.map(u => ({
                                            key: u.id,
                                            text: `${u.first_name} ${u.last_name} (${u.chapter} ${u.year})`,
                                            value: u.id
                                        }))
                                    ]}
                                    value={userForm.big_brother_user_id || 0}
                                    onChange={(e, { value }) => handleFormChange("big_brother_user_id", value || 0)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        {hasAdminPermissions && (
                            <Grid.Column>
                                <Form.Field>
                                    <label>Initiation Number</label>
                                    <Input
                                        type="number"
                                        placeholder="Initiation Number"
                                        value={userForm.init_num || ""}
                                        onChange={(e, { value }) => handleFormChange("init_num", parseInt(value) || 0)}
                                    />
                                </Form.Field>
                            </Grid.Column>
                        )}
                        {hasAdminPermissions && (
                            <Grid.Column>
                                <Form.Field>
                                    <label>MIT Alum ID</label>
                                    <Input
                                        placeholder="MIT Alumni ID"
                                        value={userForm.mit_alum_id}
                                        onChange={(e, { value }) => handleFormChange("mit_alum_id", value)}
                                    />
                                </Form.Field>
                            </Grid.Column>
                        )}
                    </Grid>

                    <Header as="h4">Contact Information</Header>
                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <Form.Field>
                                <label>Email</label>
                                <Input
                                    type="email"
                                    placeholder="Email Address"
                                    value={userForm.email}
                                    onChange={(e, { value }) => handleFormChange("email", value)}
                                />
                                <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                    IMPORTANT: this address is used for your login and all @lambdaphi.org mailing lists.
                                </div>
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Alternate Email 1</label>
                                <Input
                                    type="email"
                                    placeholder="Alternate Email"
                                    value={userForm.alternate_email_1}
                                    onChange={(e, { value }) => handleFormChange("alternate_email_1", value)}
                                />
                                <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                    This is an alternate email also allowed to send to any restricted list you're a member of. It is not visible to other members in the alumni database.
                                </div>
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Alternate Email 2</label>
                                <Input
                                    type="email"
                                    placeholder="Second Alternate Email"
                                    value={userForm.alternate_email_2}
                                    onChange={(e, { value }) => handleFormChange("alternate_email_2", value)}
                                />
                                <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                    This is a second alternate email.
                                </div>
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <Form.Field>
                                <label>Home Phone</label>
                                <Input
                                    placeholder="ie 617-555-1212"
                                    value={userForm.home_phone}
                                    onChange={(e, { value }) => handleFormChange("home_phone", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Work Phone</label>
                                <Input
                                    placeholder="ie 617-555-1212"
                                    value={userForm.work_phone}
                                    onChange={(e, { value }) => handleFormChange("work_phone", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Cell Phone</label>
                                <Input
                                    placeholder="ie 617-555-1212"
                                    value={userForm.cell_phone}
                                    onChange={(e, { value }) => handleFormChange("cell_phone", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    <Header as="h4">Address</Header>
                    <Grid columns={2} stackable>
                        <Grid.Column>
                            <Form.Field>
                                <label>Address Line 1</label>
                                <Input
                                    placeholder="Street Address"
                                    value={userForm.address_line_1}
                                    onChange={(e, { value }) => handleFormChange("address_line_1", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Address Line 2</label>
                                <Input
                                    placeholder="Apartment, Suite, etc."
                                    value={userForm.address_line_2}
                                    onChange={(e, { value }) => handleFormChange("address_line_2", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    <Grid columns={4} stackable>
                        <Grid.Column>
                            <Form.Field>
                                <label>City</label>
                                <Input
                                    placeholder="City"
                                    value={userForm.city}
                                    onChange={(e, { value }) => handleFormChange("city", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>State</label>
                                <Input
                                    placeholder="State"
                                    value={userForm.state}
                                    onChange={(e, { value }) => handleFormChange("state", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>ZIP Code</label>
                                <Input
                                    placeholder="ZIP Code"
                                    value={userForm.zip}
                                    onChange={(e, { value }) => handleFormChange("zip", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Country</label>
                                <Input
                                    placeholder="Country"
                                    value={userForm.country}
                                    onChange={(e, { value }) => handleFormChange("country", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    <Header as="h4">Professional Information</Header>
                    <Grid columns={3} stackable>
                        <Grid.Column>
                            <Form.Field>
                                <label>Company</label>
                                <Input
                                    placeholder="Company/Organization"
                                    value={userForm.company}
                                    onChange={(e, { value }) => handleFormChange("company", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Job Title</label>
                                <Input
                                    placeholder="Job Title"
                                    value={userForm.title}
                                    onChange={(e, { value }) => handleFormChange("title", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Web Page</label>
                                <Input
                                    placeholder="ie http://www.myname.com"
                                    value={userForm.web_page}
                                    onChange={(e, { value }) => handleFormChange("web_page", value)}
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    {/* Privacy section - only show for own user or if user admin */}
                    {(location.pathname === "/myaccount" || hasAdminPermissions) && (
                        <>
                            <Header as="h4">Settings</Header>
                            <Grid columns={2} stackable>
                                <Grid.Column>
                                    <Form.Field>
                                        <label>Privacy Setting</label>
                                        <Dropdown
                                            placeholder="Select Privacy Level"
                                            fluid
                                            selection
                                            options={[
                                                { key: "no lp", text: "Available only to LP Alumni Relations", value: "no lp" },
                                                { key: "lp only", text: "Available to LP Brothers", value: "lp only" },
                                                { key: "intl", text: "Available to LP Brothers & ADP Int'l", value: "intl" }
                                            ]}
                                            value={userForm.privacy_setting}
                                            onChange={(e, { value }) => handleFormChange("privacy_setting", value)}
                                        />
                                        <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                            Controls who can see your contact information.
                                        </div>
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column>
                                    <Form.Field>
                                        <label>Quarterly Emails</label>
                                        <Dropdown
                                            placeholder="Select Email Preference"
                                            fluid
                                            selection
                                            options={[
                                                { key: "true", text: "Send quarterly email", value: "true" },
                                                { key: "false", text: "Don't send quarterly email", value: "false" }
                                            ]}
                                            value={userForm.send_monthly_email}
                                            onChange={(e, { value }) => handleFormChange("send_monthly_email", value)}
                                        />
                                        <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                            Controls whether the web system will send emails to you once per quarter.
                                        </div>
                                    </Form.Field>
                                </Grid.Column>
                            </Grid>
                        </>
                    )}

                    {hasAdminPermissions && (
                        <Form.Field>
                            <Checkbox
                                label="Is Corporation Member"
                                checked={userForm.is_corporation_member}
                                onChange={(e, { checked }) => handleFormChange("is_corporation_member", checked)}
                            />
                            <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                Whether this user has been inducted into the corporation (generally after graduation.)
                            </div>
                        </Form.Field>
                    )}

                    {hasAdminPermissions && (
                        <Form.Field>
                            <label>User Type</label>
                            <Dropdown
                                placeholder="Select User Type"
                                fluid
                                selection
                                options={[
                                    { key: "normal", text: "Normal User", value: "normal" },
                                    { key: "admin", text: "LP.org Admin", value: "admin" },
                                    { key: "deceased", text: "Deceased", value: "deceased" },
                                    { key: "exile", text: "Exiled", value: "exile" }
                                ]}
                                value={userForm.type}
                                onChange={(e, { value }) => handleFormChange("type", value)}
                            />
                        </Form.Field>
                    )}

                    <Header as="h4">Additional Information</Header>
                    <Grid columns={hasAdminPermissions ? 2 : 1} stackable>
                        <Grid.Column>
                            <Form.Field>
                                <label>Notes</label>
                                <TextArea
                                    placeholder="Public notes (visible to others)"
                                    rows={3}
                                    value={userForm.notes}
                                    onChange={(e, { value }) => handleFormChange("notes", value)}
                                />
                                <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                    Will be visible to others.
                                </div>
                            </Form.Field>
                        </Grid.Column>
                        {hasAdminPermissions && (
                            <Grid.Column>
                                <Form.Field>
                                    <label>Admin Notes</label>
                                    <TextArea
                                        placeholder="Admin-only notes"
                                        rows={3}
                                        value={userForm.admin_notes}
                                        onChange={(e, { value }) => handleFormChange("admin_notes", value)}
                                    />
                                    <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25em' }}>
                                        Will be visible only to user admins.
                                    </div>
                                </Form.Field>
                            </Grid.Column>
                        )}
                    </Grid>

                    <Divider />

                    <Button.Group>
                        <Button type="submit" primary loading={loading}>
                            <Icon name="save" />
                            {location.pathname === "/myaccount" && !isViewMode ? "Save Changes" : actualUserId ? "Update User" : "Create User"}
                        </Button>
                        <Button onClick={() => {
                            if (location.pathname === "/myaccount" && !isViewMode) {
                                setIsMyAccountEditMode(false);
                            } else {
                                history.push("/users");
                            }
                        }}>
                            <Icon name="cancel" />
                            Cancel
                        </Button>
                    </Button.Group>
                </Form>
            </Segment>
        );
    }

    if (loading) {
        return (
            <Container>
                <Loader active size="large">
                    Loading...
                </Loader>
            </Container>
        );
    }

    return (
        <Container>
            <Header as="h2">
                <Icon name="users" />
                <Header.Content>
                    {location.pathname === "/myaccount" ? "My Account" : isViewMode ? "View User" : actualUserId ? "Edit User" : "Create New User"}
                    <Header.Subheader>
                        {location.pathname === "/myaccount" ? "View and manage your account information" : isViewMode ? "View user information" : actualUserId ? "Modify existing user information" : "Add a new user to the database"}
                    </Header.Subheader>
                </Header.Content>
            </Header>

            <div style={{ marginBottom: "1rem" }}>
                <Button.Group size="small">
                    <Button onClick={() => history.push(location.pathname === "/myaccount" ? "/" : "/users")}>
                        <Icon name="arrow left" />
                        {location.pathname === "/myaccount" ? "Back to Home" : "Back to User List"}
                    </Button>
                    {isViewMode && (actualUserId || location.pathname === "/myaccount") && canEdit && (
                        <Button onClick={() => {
                            if (location.pathname === "/myaccount") {
                                setIsMyAccountEditMode(true);
                            } else {
                                history.push(`/users/${actualUserId}/edit`);
                            }
                        }}>
                            <Icon name="edit" />
                            Edit User
                        </Button>
                    )}
                    {!isViewMode && (actualUserId || location.pathname === "/myaccount") && (
                        <Button onClick={() => {
                            if (location.pathname === "/myaccount") {
                                setIsMyAccountEditMode(false);
                            } else {
                                history.push(`/users/${actualUserId}`);
                            }
                        }}>
                            <Icon name="eye" />
                            View User
                        </Button>
                    )}
                    {hasAdminPermissions && (actualUserId || location.pathname === "/myaccount") && (
                        <Button onClick={() => {
                            const userId = actualUserId || loginInfo.id;
                            loadUserHistory(userId);
                            setShowHistoryModal(true);
                        }}>
                            <Icon name="history" />
                            View History
                        </Button>
                    )}
                </Button.Group>
            </div>

            {renderUserForm()}

            {/* User History Modal */}
            <Modal
                open={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                size="large"
                closeIcon
            >
                <Modal.Header>
                    <Icon name="history" />
                    User History - {userForm.first_name} {userForm.last_name}
                </Modal.Header>
                <Modal.Content>
                    {historyLoading ? (
                        <Loader active size="large">
                            Loading history...
                        </Loader>
                    ) : userHistory.length === 0 ? (
                        <Message info>
                            <Message.Header>No History Found</Message.Header>
                            <p>No changes have been recorded for this user.</p>
                        </Message>
                    ) : (
                        <Table celled>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Date/Time</Table.HeaderCell>
                                    <Table.HeaderCell>Modified By</Table.HeaderCell>
                                    <Table.HeaderCell>Changes</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {userHistory.map((entry) => (
                                    <Table.Row key={entry.id}>
                                        <Table.Cell>
                                            {new Date(entry.time).toLocaleString()}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {entry.modified_by_user.id === (actualUserId || loginInfo.id) ? (
                                                "Self"
                                            ) : entry.modified_by_user ? (
                                                <a href={`/users/${entry.modified_by_user.id}`}>
                                                    {entry.modified_by_user.first_name} {entry.modified_by_user.last_name}
                                                </a>
                                            ) : (
                                                `Data error: unknown user`
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div style={{ whiteSpace: "pre-line" }}>
                                                <ReactMarkdown>
                                                    {convertLegacyText(entry.text)}
                                                </ReactMarkdown>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setShowHistoryModal(false)}>
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>
        </Container>
    );
}
